import { fetchClient } from "@/api/api";
import type {
	createPaymentOperatorInput,
	createPaymentOperatorOutput,
	listPaymentOperatorsOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PAYMENT_OPERATORS_SERVICE_KEYS } from "./keys";

export const useListPaymentOperators: CustomQueryHook<
	listPaymentOperatorsOutput,
	undefined
> = (options) => {
	return useQuery<listPaymentOperatorsOutput, ApiError>({
		...options,
		queryKey: PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/payment-operator/",
				{
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useCreatePaymentOperator: CustomMutationHook<
	createPaymentOperatorInput,
	createPaymentOperatorOutput
> = (options) => {
	const { toastApiError } = useApiError();

	return useMutation<
		createPaymentOperatorOutput,
		ApiError,
		createPaymentOperatorInput
	>({
		...options,
		mutationKey: PAYMENT_OPERATORS_SERVICE_KEYS.useCreatePaymentOperator(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/payment-operator/",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: async (...args) => {
			invalidateQueries(
				PAYMENT_OPERATORS_SERVICE_KEYS.useListPaymentOperators(),
			);
			options?.onSuccess?.(...args);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			toastApiError(error);
			options?.onError?.(error, variables, onMutateResult, mutationContext);
		},
	});
};
